export type OwnerProjectStatus =
  | 'draft'
  | 'open_for_bid'
  | 'proposal_review'
  | 'accepted'
  | 'converted_to_manpro'
  | 'in_delivery'
  | 'completed';

export type OwnerBid = {
  id: string;
  providerId?: string;
  servicemanId?: string;
  userId?: string;
  providerName: string;
  amount: number;
  duration: string;
  note: string;
  methodStatement?: string;
  documents?: OwnerBidDocument[];
  submittedAt?: string;
  status: 'submitted' | 'accepted' | 'rejected';
};

export type OwnerBidDocumentCategory =
  | 'portfolio'
  | 'company_profile'
  | 'rab'
  | 'method'
  | 'legal'
  | 'other';

export type OwnerBidDocument = {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  category: OwnerBidDocumentCategory;
};

export type SavedVendor = {
  id: string;
  name: string;
  category: string;
  location: string;
  rating: number;
  reviews: number;
  projects: number;
  teamSize: string;
  response: string;
  badge: string;
  description: string;
  tags: string[];
  source?: 'manual' | 'bid' | 'favorite';
  savedAt: string;
};

export type OwnerMessage = {
  id: string;
  sender: 'owner' | 'provider' | 'system';
  body: string;
  createdAt: string;
};

export type OwnerProjectDocument = {
  id: string;
  name: string;
  size: number;
  type: string;
  uploadedAt: string;
  filePath?: string;
  category?: string;
};

export type OwnerProject = {
  id: string;
  title: string;
  category: string;
  location: string;
  addressDetail: string;
  latitude?: number;
  longitude?: number;
  budgetCeiling: number;
  budgetRange?: string;
  areaSize?: string;
  currentCondition?: string;
  priority?: string;
  procurementType?: string;
  province?: string;
  city?: string;
  district?: string;
  postalCode?: string;
  startPreference?: string;
  bidDeadline?: string;
  clarificationDate?: string;
  estimatedStart?: string;
  estimatedFinish?: string;
  duration?: string;
  executionMethod?: string;
  workHours?: string;
  selectedNeeds?: string[];
  publicationType?: string;
  audience?: string;
  publishDuration?: string;
  description: string;
  requirements: string[];
  documents: OwnerProjectDocument[];
  status: OwnerProjectStatus;
  progress: number;
  selectedProvider?: string;
  invitedVendorIds?: string[];
  manpro_project?: any;
  manproProject?: any;
  manproProjectId?: string;
  createdAt: string;
  bids: OwnerBid[];
  messages: OwnerMessage[];
};

type StoredOwnerProject = Omit<OwnerProject, 'documents'> & {
  documents: Array<OwnerProjectDocument | string>;
};

export type ManproDraftStatus =
  | 'draft'
  | 'baseline_saved'
  | 'active'
  | 'execution'
  | 'validation'
  | 'completed'
  | 'reporting';

export type ManproDraftProject = {
  id: string;
  sourceOwnerProjectId: string;
  title: string;
  ownerName: string;
  providerId: string;
  providerName: string;
  category: string;
  location: string;
  addressDetail: string;
  budget: number;
  documents: OwnerProjectDocument[];
  status: ManproDraftStatus;
  createdAt: string;
};

export type PublicOpportunity = {
  id: string;
  title: string;
  category: string;
  location: string;
  budgetRange: string;
  ownerType: string;
  deadline: string;
  summary: string;
};

const OWNER_PROJECTS_KEY = 'owner_portal_projects_v1';
const OWNER_SAVED_OPPORTUNITIES_KEY = 'owner_saved_opportunities_v1';
const OWNER_SAVED_VENDORS_KEY = 'owner_saved_vendors_v1';
const MANPRO_DRAFT_PROJECTS_KEY = 'manpro_draft_projects_v1';

export const defaultSavedVendors: SavedVendor[] = [
  {
    id: 'vendor-pt-tekno-karya-interior',
    name: 'PT Tekno Karya Interior',
    category: 'Interior, Renovasi, Fit-Out',
    location: 'Jakarta Selatan',
    rating: 4.9,
    reviews: 128,
    projects: 42,
    teamSize: '35+ tenaga ahli',
    response: '< 2 jam',
    badge: 'Verified Partner',
    description: 'Vendor spesialis interior komersial, renovasi kantor, rumah premium, dan fit-out retail.',
    tags: ['Interior', 'Renovasi', 'MEP Minor'],
    source: 'manual',
    savedAt: new Date().toISOString(),
  },
  {
    id: 'vendor-indo-facility-build',
    name: 'Indo Facility Build',
    category: 'Konstruksi Sipil & Maintenance',
    location: 'Bekasi',
    rating: 4.8,
    reviews: 96,
    projects: 38,
    teamSize: '60+ tenaga ahli',
    response: '< 4 jam',
    badge: 'Top Rated',
    description: 'Partner konstruksi sipil untuk gudang, ruko, perbaikan struktur, dan pekerjaan fasilitas.',
    tags: ['Sipil', 'Gudang', 'Maintenance'],
    source: 'manual',
    savedAt: new Date().toISOString(),
  },
  {
    id: 'vendor-arsitek-nusantara-studio',
    name: 'Arsitek Nusantara Studio',
    category: 'Arsitektur & Desain',
    location: 'DKI Jakarta',
    rating: 4.7,
    reviews: 74,
    projects: 29,
    teamSize: '12 arsitek',
    response: '< 1 hari',
    badge: 'Design Expert',
    description: 'Studio desain arsitektur dengan layanan konsep, gambar kerja, RAB, dan pendampingan tender.',
    tags: ['Arsitektur', '3D Design', 'RAB'],
    source: 'manual',
    savedAt: new Date().toISOString(),
  },
];

export const publicOpportunities: PublicOpportunity[] = [
  {
    id: 'opp-1',
    title: 'Pembangunan Gedung Kantor 6 Lantai',
    category: 'Konstruksi Gedung',
    location: 'Jakarta Selatan, DKI Jakarta',
    budgetRange: 'Rp 8.000.000.000 - Rp 12.000.000.000',
    ownerType: 'Tender Pemerintah',
    deadline: '14 hari lagi',
    summary: 'Pekerjaan konstruksi gedung kantor lengkap dari struktur, arsitektur, hingga MEP.',
  },
  {
    id: 'opp-2',
    title: 'Pekerjaan Renovasi Pabrik Area Produksi',
    category: 'Renovasi Pabrik',
    location: 'Cikarang, Jawa Barat',
    budgetRange: 'Rp 2.000.000.000 - Rp 3.000.000.000',
    ownerType: 'BUMN',
    deadline: '9 hari lagi',
    summary: 'Renovasi area produksi, perbaikan utilitas, elektrikal, HVAC, dan finishing industri.',
  },
  {
    id: 'opp-3',
    title: 'Renovasi Rumah Type 90',
    category: 'Renovasi Rumah',
    location: 'Depok, Jawa Barat',
    budgetRange: 'Rp 100.000.000 - Rp 150.000.000',
    ownerType: 'Proyek Owner',
    deadline: '21 hari lagi',
    summary: 'Renovasi rumah tinggal meliputi tukang, plumbing, elektrikal, dan finishing.',
  },
  {
    id: 'opp-4',
    title: 'Kebutuhan Subkontraktor Plafon & Partisi',
    category: 'Interior',
    location: 'Surabaya, Jawa Timur',
    budgetRange: 'Rp 30.000.000',
    ownerType: 'Kebutuhan Subkontraktor',
    deadline: '7 hari lagi',
    summary: 'Pekerjaan plafon gypsum, partisi, dan aksen dinding untuk area kantor.',
  },
  {
    id: 'opp-5',
    title: 'Pengadaan & Instalasi Solar Panel 100 kWp',
    category: 'MEP / Elektrikal',
    location: 'Bandung, Jawa Barat',
    budgetRange: 'Rp 500.000.000 - Rp 700.000.000',
    ownerType: 'RFQ',
    deadline: '3 hari lagi',
    summary: 'Pengadaan panel surya, instalasi, testing, dan commissioning sistem.',
  },
  {
    id: 'opp-6',
    title: 'Pembangunan Jalan Akses Kawasan Industri',
    category: 'Konstruksi Sipil',
    location: 'Subang, Jawa Barat',
    budgetRange: 'Rp 15.000.000.000 - Rp 20.000.000.000',
    ownerType: 'Tender Terbatas',
    deadline: '18 hari lagi',
    summary: 'Pekerjaan jalan akses kawasan industri, drainase, jembatan kecil, dan aspal.',
  },
];

const createSeedProjects = (): OwnerProject[] => [
  {
    id: 'owner-project-1',
    title: 'Renovasi Area Resepsionis Kantor Pusat',
    category: 'Renovasi',
    location: 'Jakarta Pusat',
    addressDetail: 'Jl. Sudirman Kav. 12, lantai dasar',
    latitude: -6.208763,
    longitude: 106.845599,
    budgetCeiling: 450000000,
    description:
      'Peremajaan area resepsionis, perbaikan plafon, lighting, signage, dan area tunggu.',
    requirements: ['Pekerjaan malam hari', 'Dokumen metode kerja', 'Garansi finishing 3 bulan'],
    documents: [
      {
        id: 'seed-doc-1',
        name: 'brief-renovasi.pdf',
        size: 1850000,
        type: 'application/pdf',
        uploadedAt: new Date().toISOString(),
      },
      {
        id: 'seed-doc-2',
        name: 'layout-lobby.dwg',
        size: 3200000,
        type: 'application/octet-stream',
        uploadedAt: new Date().toISOString(),
      },
    ],
    status: 'proposal_review',
    progress: 0,
    createdAt: new Date().toISOString(),
    bids: [
      {
        id: 'bid-1',
        providerName: 'PT Tekno Karya Interior',
        amount: 425000000,
        duration: '38 hari',
        note: 'Termasuk material finishing premium dan pengawasan harian.',
        status: 'submitted',
      },
      {
        id: 'bid-2',
        providerName: 'Indo Facility Build',
        amount: 398000000,
        duration: '45 hari',
        note: 'Opsi value engineering untuk menekan biaya tanpa mengurangi SLA.',
        status: 'submitted',
      },
    ],
    messages: [
      {
        id: 'msg-1',
        sender: 'system',
        body: 'Proyek sudah dibuka untuk penawaran provider.',
        createdAt: new Date().toISOString(),
      },
      {
        id: 'msg-2',
        sender: 'provider',
        body: 'Kami sudah mengirim estimasi biaya dan durasi pekerjaan.',
        createdAt: new Date().toISOString(),
      },
    ],
  },
];

const readJson = <T>(key: string, fallback: T): T => {
  const raw = localStorage.getItem(key);

  if (!raw) {
    return fallback;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const normalizeOwnerProject = (project: StoredOwnerProject): OwnerProject => ({
  ...project,
  documents: (project.documents ?? []).map((document, index) => {
    if (typeof document === 'string') {
      return {
        id: `${project.id}-doc-${index + 1}`,
        name: document,
        size: 0,
        type: 'application/octet-stream',
        uploadedAt: project.createdAt,
      };
    }

    return document;
  }),
});

const normalizeBid = (bid: OwnerBid): OwnerBid => ({
  ...bid,
  providerId: bid.providerId || `provider-${bid.providerName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
  submittedAt: bid.submittedAt || new Date().toISOString(),
  documents: bid.documents ?? [],
});

export const getOwnerProjects = () => {
  const projects = readJson<StoredOwnerProject[]>(OWNER_PROJECTS_KEY, []);

  if (projects.length > 0) {
    const normalizedProjects = projects.map((project) => ({
      ...normalizeOwnerProject(project),
      bids: (project.bids ?? []).map(normalizeBid),
    }));
    localStorage.setItem(OWNER_PROJECTS_KEY, JSON.stringify(normalizedProjects));
    return normalizedProjects;
  }

  const seeded = createSeedProjects();
  localStorage.setItem(OWNER_PROJECTS_KEY, JSON.stringify(seeded));
  return seeded;
};

export const saveOwnerProjects = (projects: OwnerProject[]) => {
  localStorage.setItem(OWNER_PROJECTS_KEY, JSON.stringify(projects));
  window.dispatchEvent(new Event('owner-projects-updated'));
};

export const getManproDraftProjects = () =>
  readJson<ManproDraftProject[]>(MANPRO_DRAFT_PROJECTS_KEY, []);

export const saveManproDraftProjects = (projects: ManproDraftProject[]) => {
  localStorage.setItem(MANPRO_DRAFT_PROJECTS_KEY, JSON.stringify(projects));
  window.dispatchEvent(new Event('manpro-drafts-updated'));
};

export const createManproDraftFromOwnerProject = (
  project: OwnerProject,
  acceptedBid: OwnerBid,
) => {
  const drafts = getManproDraftProjects();
  const existingDraft = drafts.find((draft) => draft.sourceOwnerProjectId === project.id);

  if (existingDraft) {
    return existingDraft;
  }

  const draft: ManproDraftProject = {
    id: `manpro-draft-${Date.now()}`,
    sourceOwnerProjectId: project.id,
    title: project.title,
    ownerName: 'Project Owner',
    providerId: acceptedBid.providerId || 'prototype-provider',
    providerName: acceptedBid.providerName,
    category: project.category,
    location: project.location,
    addressDetail: project.addressDetail,
    budget: acceptedBid.amount,
    documents: project.documents,
    status: 'draft',
    createdAt: new Date().toISOString(),
  };

  saveManproDraftProjects([draft, ...drafts]);
  return draft;
};

export const getSavedOpportunityIds = () =>
  readJson<string[]>(OWNER_SAVED_OPPORTUNITIES_KEY, []);

export const saveOpportunityIds = (ids: string[]) => {
  localStorage.setItem(OWNER_SAVED_OPPORTUNITIES_KEY, JSON.stringify(ids));
};

export const getSavedVendors = () => {
  const vendors = readJson<SavedVendor[]>(OWNER_SAVED_VENDORS_KEY, []);

  if (vendors.length > 0) return vendors;

  localStorage.setItem(OWNER_SAVED_VENDORS_KEY, JSON.stringify(defaultSavedVendors));
  return defaultSavedVendors;
};

export const saveSavedVendors = (vendors: SavedVendor[]) => {
  localStorage.setItem(OWNER_SAVED_VENDORS_KEY, JSON.stringify(vendors));
  window.dispatchEvent(new Event('owner-saved-vendors-updated'));
};

export const createVendorId = (name: string) =>
  `vendor-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`;

export const upsertSavedVendor = (vendor: Omit<SavedVendor, 'id' | 'savedAt'> & Partial<Pick<SavedVendor, 'id' | 'savedAt'>>) => {
  const vendors = getSavedVendors();
  const id = vendor.id || createVendorId(vendor.name);
  const nextVendor: SavedVendor = {
    ...vendor,
    id,
    savedAt: vendor.savedAt || new Date().toISOString(),
  };
  const exists = vendors.some((item) => item.id === id || item.name === vendor.name);
  const nextVendors = exists
    ? vendors.map((item) => (item.id === id || item.name === vendor.name ? { ...item, ...nextVendor } : item))
    : [nextVendor, ...vendors];

  saveSavedVendors(nextVendors);
  return nextVendor;
};

export const formatCurrency = (value: number) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(value);

export const getStatusLabel = (status: OwnerProjectStatus) => {
  const labels: Record<OwnerProjectStatus, string> = {
    draft: 'Draft',
    open_for_bid: 'Menunggu Penawaran',
    proposal_review: 'Review Penawaran',
    accepted: 'Masuk MANPRO / Monitoring',
    converted_to_manpro: 'Masuk MANPRO / Monitoring',
    in_delivery: 'Monitoring Delivery',
    completed: 'Selesai',
  };

  return labels[status];
};
